import {checkAndCutByBytes} from "@utils/string";
import {environment} from "@env";
import {findLastElements} from "@utils/array";
import {
    CHAT_ACTION_NAME,
    ChatActionBase,
    IChatActionList,
    IChatActionsData, IChatInputActionItem
} from "@core/base/chat-action-base";
import { ICoreDependencies } from "@core/core-boot/core-boot.types";
import { OfficeChat } from "@core/office/office-chat/office-chat";
import { ChatUtilsService } from "@core/shared/chat-utils.service";
import { OfficeService } from "@core/office/office-service/office-service";
import { IChatMessageUser } from "@listat/r2-core";

export class OfficeChatAction implements ChatActionBase {
    private clipboard = this.dependencies.clipboard;

    public action: IChatActionList = {
        [CHAT_ACTION_NAME.selection]: {
            value: false,
            onBuildData: this.buildDataForSelection
        },
        [CHAT_ACTION_NAME.attachDocument]: {
            value: false,
            onBuildData: this.buildDataForAttachDocument
        },
        [CHAT_ACTION_NAME.clipboard]: {
            value: false,
            onBuildData: this.buildDataForClipboard
        },
        [CHAT_ACTION_NAME.history]: {
            value: false,
            onBuildData: this.buildDataForHistory
        },
        [CHAT_ACTION_NAME.img]: {
            value: false,
            onBuildData: this.buildDataForImg
        }
    }
    private actionData: IChatActionsData = {
        attachDocument: '',
        img: false,
        history: [],
        selection: '',
        clipboard: ''
    }

    constructor(
        private dependencies: ICoreDependencies,
        private officeService: OfficeService,
        private chatService: OfficeChat,
        private chatUtils: ChatUtilsService,
    ) {}

    public setTextToHost(text: string): void {
        this.officeService.setSelectedTextAsync(text);
    }

    public setImageToHost(base64Image: string): void {
        if (!base64Image) {
            throw new TypeError('Base64 string is not defined')
        }

        const base64StartIndex = base64Image.indexOf(';base64,');
        const base64 = base64Image.substring(base64StartIndex + 8);
        const imageMimetype = base64Image.substring(5, base64StartIndex);
        const imageExtension = imageMimetype.split('/')[1];
        const fileName = `image.${imageExtension}`;

        this.officeService.setSelectedImageAsync(fileName, base64);
    }

    public isReadableDocumentHost(): boolean {
        const host = this.officeService.getHost();
        return host.checkDocumentReadable();
    }

    public isEditableHost(): boolean {
        const host = this.officeService.getHost();
        return host.checkEditable();
    }

    public async getSelectedContentFromHost(): Promise<string> {
        const host = this.officeService.getHost();
        return host.getSelectedContentAsync()
    }

    public async getFullDocumentFromHost(): Promise<string> {
        const host = this.officeService.getHost();
        return host.getDocumentTextAsync()
    }

    public getActions(): IChatActionList {
        return this.action;
    }

    public async getActionsData(): Promise<IChatActionsData> {
        for (const [key, item] of Object.entries(this.action)) {
            await item.onBuildData.call(this, item)
        }
        return this.actionData
    }

    private async buildDataForSelection(action: IChatInputActionItem): Promise<void> {
        if (action.value) {
            const data = await this.getSelectedContentFromHost();
            this.actionData.selection = await checkAndCutByBytes(data, environment.limits_bytes.ATTACH_SELECTED);
            return
        }
        this.actionData.selection = '';
    }

    private async buildDataForAttachDocument(action: IChatInputActionItem): Promise<void> {
        if (action.value) {
            const data = await this.getFullDocumentFromHost();
            this.actionData.attachDocument = await checkAndCutByBytes(data, environment.limits_bytes.ATTACH_DOCUMENT);
            return
        }
        this.actionData.attachDocument = '';
    }

    private async buildDataForClipboard(action: IChatInputActionItem): Promise<void> {
        if (action.value) {
            const data = await this.clipboard.copyFrom();
            this.actionData.clipboard = data || '';
            return
        }
        this.actionData.clipboard = '';
    }

    private buildDataForHistory(action: IChatInputActionItem): void {
        const messages = this.chatService.getMessagesValue();

        if (action.value) {
            const historyCount = environment.history_count_message;
            const lastMessages = findLastElements(messages, historyCount);
            this.actionData.history = lastMessages.reduce<typeof this.actionData.history>((result, item) => {
                const isBotMessage = this.chatUtils.isSenderBot
                const isValueImage = this.chatUtils.isValueImg.bind(this.chatUtils)
                if (isBotMessage(item) && isValueImage(item)) {
                    // TODO ask question about,will image send for history?
                    return result
                }

                const value = item.value || (item as IChatMessageUser).metadata?.quickAction?.value || (item as IChatMessageUser).metadata?.skill?.prompt || '';

                result.push({ role: item.sender, content: value })
                return result
            }, []);
            return;
        }

        this.actionData.history = []
    }

    private buildDataForImg(action: IChatInputActionItem): void {
        if (action.value) {
            this.actionData.img = true;
            return
        }
        this.actionData.img = false;
    }
}
